/**
 * @rogueworlds/voxel-bridge — the block registry.
 *
 * Every id is a small integer so a chunk can be stored as palette indices in a `Uint8Array` and
 * shipped as `base64(rle(...))` (see CONTRACTS.md "packages/voxel-bridge"). The registry is a
 * frozen, ordered array: `BLOCKS[id].id === id`, which is what lets the mesher treat `BlockId` as
 * an atlas tile index without a second lookup table.
 *
 * ISOMORPHIC: no DOM, no `three`, no Node built-ins, no randomness. This module is imported by the
 * browser client, a Web Worker and the Node authority process alike.
 */

import type { BlockId } from "@rogueworlds/shared";

export const AIR: BlockId = 0;
export const STONE: BlockId = 1;
export const DIRT: BlockId = 2;
export const GRASS: BlockId = 3;
export const SAND: BlockId = 4;
export const WATER: BlockId = 5;
export const LOG: BlockId = 6;
export const LEAVES: BlockId = 7;
export const PLANK: BlockId = 8;
export const GLASS: BlockId = 9;
export const SNOW: BlockId = 10;
export const BASALT: BlockId = 11;

/** Every known block id, in ascending order. `BLOCK_IDS.length === BLOCKS.length`. */
export const BLOCK_IDS: readonly BlockId[] = [
  AIR,
  STONE,
  DIRT,
  GRASS,
  SAND,
  WATER,
  LOG,
  LEAVES,
  PLANK,
  GLASS,
  SNOW,
  BASALT,
];

/** Renderer/physics-relevant facts about a block. `color` is the fallback vertex tint (0..1). */
export interface BlockDef {
  readonly id: BlockId;
  readonly name: string;
  readonly solid: boolean;
  readonly opaque: boolean;
  readonly color: readonly [number, number, number];
}

/**
 * The registry. Index === id, length 12 (checked by a test). `opaque` drives face emission in the
 * greedy mesher; `solid` drives collision in `packages/sim`. AIR, WATER and GLASS are not solid;
 * WATER and GLASS are additionally not opaque (they do not cull their neighbours' faces).
 */
export const BLOCKS: readonly BlockDef[] = [
  { id: AIR, name: "air", solid: false, opaque: false, color: [0, 0, 0] },
  { id: STONE, name: "stone", solid: true, opaque: true, color: [0.52, 0.52, 0.55] },
  { id: DIRT, name: "dirt", solid: true, opaque: true, color: [0.47, 0.35, 0.24] },
  { id: GRASS, name: "grass", solid: true, opaque: true, color: [0.35, 0.62, 0.26] },
  { id: SAND, name: "sand", solid: true, opaque: true, color: [0.85, 0.78, 0.52] },
  { id: WATER, name: "water", solid: false, opaque: false, color: [0.19, 0.42, 0.72] },
  { id: LOG, name: "log", solid: true, opaque: true, color: [0.38, 0.28, 0.16] },
  { id: LEAVES, name: "leaves", solid: true, opaque: true, color: [0.22, 0.46, 0.18] },
  { id: PLANK, name: "plank", solid: true, opaque: true, color: [0.68, 0.52, 0.32] },
  { id: GLASS, name: "glass", solid: false, opaque: false, color: [0.72, 0.85, 0.88] },
  { id: SNOW, name: "snow", solid: true, opaque: true, color: [0.92, 0.94, 0.97] },
  { id: BASALT, name: "basalt", solid: true, opaque: true, color: [0.22, 0.22, 0.25] },
];

/**
 * True when the block blocks movement. Unknown ids (out of registry range) are treated as not
 * solid: an unknown block is a bug in the caller, not a wall in the world.
 */
export function isSolid(block: BlockId): boolean {
  const def = BLOCKS[block];
  return def !== undefined && def.solid;
}

/**
 * True when the block hides the face of the voxel behind it. `noUncheckedIndexedAccess` makes the
 * lookup `BlockDef | undefined`, so an unknown id is explicitly "not opaque".
 */
export function isOpaque(block: BlockId): boolean {
  const def = BLOCKS[block];
  return def !== undefined && def.opaque;
}
